// axiosInstance.js
import axios, { AxiosInstance } from "axios";
import { end_points } from "./endpoints";
import store from "../core/redux/store";
import { toast } from "react-toastify";
import {
  removeAuthToken,
  setUserDetails,
  setUserDirectoryDetails,
  setLocationPermissionModal,
} from "../core/redux/commonSlice";
import { goToLogin } from "../utils/constants";
import { trackEvent } from "../utils/dataLayer";
import { commonWords } from "@/global";
// import { getMessage } from "../utils/getMessage";
import { requestLocationPermission } from "../utils/locationPermission";
import { getMessage } from "../utils/getMessage";

export const axiosInstanceWithToken: AxiosInstance = axios.create();
const axiosInstance: AxiosInstance = axios.create();

// Endpoints whose backend historically returns a 400 specifically because
// Lat/Long is missing - shown with the distinct "enable location" info toast
// below (case 400) instead of the generic red error toast.
const LOCATION_RESPONSE_HINT_ENDPOINTS = [
  end_points.get_wellness_local_recommendations_common.url,
  end_points.ww_get_slots_data_utc.url,
  end_points.provider_near_me.url,
].filter(Boolean);

// Endpoints gated below: if no location is stored, we try to resolve it
// (which re-prompts the browser only while permission is still undecided)
// before the call goes out, so a missing/denied permission surfaces our own
// "enable location" modal instead of the call failing. Endpoints not in this
// list still receive whatever Lat/Long happens to be in localStorage, but are
// never blocked on it.
const LOCATION_REQUIRED_ENDPOINTS = [
  ...LOCATION_RESPONSE_HINT_ENDPOINTS,
  end_points.add_events.url,
  end_points.update_events.url,
].filter(Boolean);

// Coalesces concurrent gated requests into a single browser permission
// prompt/geolocation lookup instead of firing one per in-flight request.
let locationRequestInFlight: Promise<GeolocationPosition> | null = null;
const resolveLocationOnce = (): Promise<GeolocationPosition> => {
  if (!locationRequestInFlight) {
    locationRequestInFlight = requestLocationPermission().finally(() => {
      locationRequestInFlight = null;
    });
  }
  return locationRequestInFlight;
};

// Add request interceptor to axiosInstanceWithToken for tracking
axiosInstanceWithToken.interceptors.request.use(
  async (config: any) => {
    // Store request start time for tracking with high precision
    config.metadata = { startTime: performance.now() };
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.request.use(
  async (config: any) => {
    const localStorageToken = localStorage.getItem("token");
    const token = localStorageToken ? localStorageToken : "";
    const guestToken = "f0e2de5e7e94dc03c8e58223b55cd0952c5762d8f9d0d4fc84634e14d91db5e8";
    const staticTokenEndpoints = [
      end_points.master_provider_catgeory_listApi.url,
      end_points.suggestedVideosAndBlogsApi.url,
      end_points.master_category_list.url,
      end_points.writtenContent_Articles_list.url,
      end_points.providers_online_list.url,
      end_points.homepage_provider_list.url,
      end_points.home_featured_providers_list.url,
      end_points.get_worldwide_feed.url,
      end_points.category_videos_list.url,
      end_points.get_recommendedBlogs_list.url,
      end_points.writtenContent_Lists_Api.url,
      end_points.get_related_vidoes.url,
      end_points.get_relatedVideos_slug.url,
      end_points.list_feed.url,
      end_points.announcement_popupListApi.url,
      end_points.search_tag_list_api.url,
      end_points.get_singleArticle_Details.url,
      end_points.event_most_popular.url,
      end_points.events_recommended.url,
      end_points.events_trending.url,
      end_points.events_newest.url,
      end_points.stop_record.url,
      end_points.stop_record_new.url,
      end_points.user_provider_search_filter.url,
      end_points.get_providerDetail_slug.url,
      end_points.get_category_list_without_login.url,
      end_points.get_wellness_local_recommendations_common.url,
      end_points.get_latest_article.url,
      end_points.is_provider.url,
      end_points.add_location_profile_count.url,
      end_points.feture_provider_list.url,
      end_points.feature_provider_list.url,
      end_points.get_order_details.url,
      end_points.get_order_details.url,
      end_points.store_popular_city.url,
      end_points.get_popular_city.url,
      end_points.filter_video_list.url,
      end_points.new_wellness_hub.url,
      end_points.client_category_list_api.url,
      end_points.search_by_tags.url,
      end_points.get_all_videos_events.url,
      end_points.donation_create_payment_intent.url,
      end_points.donation_confirm_donation.url,
      end_points.popularVideosCategory_Api.url,
      end_points.get_top_videos.url,
      end_points.get_trending_videos.url,
      end_points.get_latest_videos.url,
      end_points.get_all_category_videos.url,
      end_points.view_profile_list_events.url,
      end_points.meeting_get_waiting_room_by_slug.url,
      end_points.ad_view.url,
      end_points.get_top_liked_videos.url,
    ];

    const needsStaticToken = staticTokenEndpoints?.some((endpoint) =>
      config.url.startsWith(endpoint),
    );
    // Retrieve the stored location
    const locationString = localStorage.getItem("location");
    const location = locationString ? JSON.parse(locationString) : null;

    // Extract lat & long
    let latitude = location?.lat || undefined;
    let longitude = location?.long || undefined;

    // For endpoints that can't return a meaningful result without Lat/Long,
    // resolve location up front instead of letting the call go out and fail.
    if (
      (!latitude || !longitude) &&
      LOCATION_REQUIRED_ENDPOINTS.some((endpoint) => config.url.startsWith(endpoint))
    ) {
      try {
        const position = await resolveLocationOnce();
        latitude = position.coords.latitude;
        longitude = position.coords.longitude;
      } catch (locationError) {
        store.dispatch(setLocationPermissionModal(true));
        return Promise.reject({
          locationPermissionRequired: true,
          message: "Location permission is required to complete this request.",
        });
      }
    }
    // eslint-disable-next-line no-undef
    config.baseURL = API_URL;
    if (
      config.url.startsWith(end_points.add_announcement.url) ||
      config.url.startsWith(end_points.update_announcement.url) ||
      config.url.startsWith(end_points.profile_image_upload.url) ||
      config.url.startsWith(end_points.profile_gallery_images_upload.url) ||
      config.url.startsWith(end_points.createVideo.url) ||
      config.url.startsWith(end_points.profile_gallery_image_update.url) ||
      config.url.startsWith(end_points.settings_license_image_upload.url) ||
      config.url.startsWith(end_points.uploadVideoBunny.url) ||
      config.url.startsWith(end_points.settings_license.url) ||
      config.url.startsWith(end_points.writtenContent_Create_Api.url) ||
      config.url.startsWith(end_points.go_live_now.url) ||
      config.url.startsWith(end_points.world_wide_stream.url) ||
      config.url.startsWith(end_points.go_schedule_live_now.url) ||
      config.url.startsWith(end_points.go_schedule_live_update.url) ||
      config.url.startsWith(end_points.settings_verification_doc_upload.url) ||
      config.url.startsWith(end_points.client_account_settings_update.url) ||
      config.url.startsWith(end_points.client_ticket_create.url) ||
      config.url.startsWith(end_points.client_ticket_update.url) ||
      config.url.startsWith(end_points.tax_form_update.url) ||
      config.url.startsWith(end_points.new_basic_information_update.url) ||
      config.url.startsWith(end_points.about_review_send_mail.url) ||
      config.url.startsWith(end_points.review_send_mail.url) ||
      config.url.startsWith(end_points.tax_form_save.url) ||
      config.url.startsWith(end_points.feture_provider_list.url) ||
      config.url.startsWith(end_points.get_related_videos.url) ||
      config.url.startsWith(end_points.new_wellness_hub.url)
      // config.url.startsWith(end_points.donation_create_payment_intent.url)
    ) {
      //add url for form-data
      config.headers = {
        ...config.headers,
        // Authorization: token ? `Bearer ${token}` : "",
        Authorization:
          needsStaticToken && !localStorageToken
            ? `Bearer ${guestToken}`
            : localStorageToken
              ? `Bearer ${localStorageToken}`
              : "",
        token: `${token}`,
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
        Lat: latitude,
        Long: longitude,
      };
    } else {
      config.headers = {
        ...config.headers,
        // Authorization: token ? `Bearer ${token}` : "",
        Authorization:
          needsStaticToken && !localStorageToken
            ? `Bearer ${guestToken}`
            : localStorageToken
              ? `Bearer ${localStorageToken}`
              : "",
        token: `${token}`,
        // "Content-Type": "application/json",
        Accept: "application/json",
        Lat: latitude,
        Long: longitude,
      };
    }

    // Store request start time for tracking with high precision
    config.metadata = { startTime: performance.now() };

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  async (response: any) => {
    // Track API call with high precision timing
    const responseTime = response?.config?.metadata?.startTime
      ? performance.now() - response.config.metadata.startTime
      : 0;

    trackEvent("api_response_time", {
      endpoint: response?.config?.url || "",
      status_code: response?.status || 0,
      response_time_ms: responseTime.toFixed(0),
    });

    await handleResponseStatus(response);
    return response;
  },
  async (error) => {
    // Track API call error with high precision timing
    const responseTime = error?.config?.metadata?.startTime
      ? performance.now() - error.config.metadata.startTime
      : 0;

    trackEvent("api_response_time", {
      endpoint: error?.config?.url || "",
      status_code: error?.response?.status || 0,
      response_time_ms: responseTime.toFixed(0),
    });

    await handleResponseStatus(error.response);
    return error?.response;
  },
);
// axiosInstanceWithToken
axiosInstanceWithToken.interceptors.response.use(
  async (response: any) => {
    // Track API call with high precision timing
    const responseTime = response?.config?.metadata?.startTime
      ? performance.now() - response.config.metadata.startTime
      : 0;

    trackEvent("api_response_time", {
      endpoint: response?.config?.url || "",
      status_code: response?.status || 0,
      response_time_ms: responseTime.toFixed(0),
    });

    await handleResponseStatus(response);
    return response;
  },
  async (error) => {
    // Track API call error with high precision timing
    const responseTime = error?.config?.metadata?.startTime
      ? performance.now() - error.config.metadata.startTime
      : 0;

    trackEvent("api_response_time", {
      endpoint: error?.config?.url || "",
      status_code: error?.response?.status || 0,
      response_time_ms: responseTime.toFixed(0),
    });

    await handleResponseStatus(error.response);
    return error?.response;
  },
);
// Central wallet balance sync
//
// The Header Profile Menu (and any other consumer) reads its balance from
// state.common.userDetails.wallet_balance_amount. Rather than have every
// page that can move the wallet balance (add funds, withdraw, subscribe,
// donate, checkout, boost a profile, ...) remember to dispatch that update
// itself, every mutating request that passes through this interceptor is
// inspected here, in one place, and the store is kept in sync automatically.
//
// Two complementary strategies are used:
//   1. Most endpoints that touch the wallet already return the updated
//      user/wallet record in their payload (the `wallet_balance_amount` /
//      `wallet_balance` field). When present, it's applied immediately with
//      no extra network round trip - this covers new wallet-affecting
//      endpoints automatically, with no list to maintain.
//   2. A small list of known financial endpoints whose success response
//      does *not* echo the balance (e.g. wallet/add-amount just returns a
//      transaction receipt) triggers a debounced refetch of the canonical
//      wallet endpoint instead.
const WALLET_BALANCE_ENDPOINTS = [
  end_points.wallet_AddAmountApi.url,
  end_points.wallet_withdraw_Api.url,
  end_points.client_wallet_withdraw_Api.url,
  end_points.add_deposit_wallet_api.url,
  end_points.wallet_wallet_deposit.url,
  // Client subscribes to / changes plan with a provider (user/subscribe-provider) -
  // the actual endpoint hit from the view-profile subscribe flow.
  end_points.updateSubscriptionPlanApi.url,
  end_points.authSubscriptionConfirmPlanApi.url,
  end_points.authSubscriptionPlanApi.url,
  end_points.authSubscriptionRenewalConfirmApi.url,
  end_points.subscription_upgradeFreePlanConfirmApi.url,
  end_points.subscription_upgradeClaimPlanConfirmApi.url,
  end_points.confirmSubscriptionPlanApi.url,
  end_points.authUpdateSubscriptionApi.url,
  end_points.downgrade_subscriptionplan.url,
  end_points.settings_subscription_plan_downgrade_Api.url,
  end_points.client_subscription_cancel.url,
  end_points.client_subscription_pause.url,
  end_points.client_subscription_resume.url,
  end_points.donation_confirm_donation.url,
  end_points.checkout_order.url,
  end_points.subscribe_promocode_apply.url,
  end_points.promocode_apply_stream.url,
  end_points.onetime_payment_create.url,
  end_points.create_campaign.url,
  end_points.update_campaign.url,
].filter(Boolean);

const pickWalletBalance = (record: any): number | undefined => {
  if (!record || typeof record !== "object") return undefined;
  const raw = record.wallet_balance_amount ?? record.wallet_balance;
  return raw !== undefined && raw !== null && raw !== "" ? Number(raw) : undefined;
};

const extractWalletBalance = (response: any): number | undefined => {
  const data = response?.data?.data;
  return Array.isArray(data) ? pickWalletBalance(data[0]) : pickWalletBalance(data);
};

const applyWalletBalance = (walletAmount: number) => {
  const state: any = store.getState();
  store.dispatch(
    setUserDetails({ ...state?.common?.userDetails, wallet_balance_amount: walletAmount }),
  );
  store.dispatch(
    setUserDirectoryDetails({
      ...state?.common?.userDirectoryDetails,
      wallet_balance_amount: walletAmount,
    }),
  );
};

let walletRefreshTimer: ReturnType<typeof setTimeout> | null = null;
let walletRefreshInFlight: Promise<void> | null = null;

// Exported so pages can also trigger an explicit refresh (e.g. right after
// opening the wallet page) without duplicating the fetch/dispatch logic.
export const refreshWalletBalance = (): Promise<void> => {
  if (!localStorage.getItem("token")) return Promise.resolve();
  if (walletRefreshInFlight) return walletRefreshInFlight;
  walletRefreshInFlight = axiosInstance
    .get(end_points.billing_walletGetCardDetailApi.url as string)
    .then((res: any) => {
      const walletAmount = extractWalletBalance(res);
      if (walletAmount !== undefined) applyWalletBalance(walletAmount);
    })
    .catch(() => {})
    .finally(() => {
      walletRefreshInFlight = null;
    });
  return walletRefreshInFlight;
};

const scheduleWalletRefresh = () => {
  if (walletRefreshTimer) clearTimeout(walletRefreshTimer);
  // Debounced so a burst of near-simultaneous mutations (e.g. a modal that
  // fires two requests on success) collapses into a single refetch.
  walletRefreshTimer = setTimeout(() => {
    walletRefreshTimer = null;
    refreshWalletBalance();
  }, 400);
};

const MUTATING_METHODS = ["post", "put", "patch", "delete"];

const maybeSyncWalletBalance = (response: any) => {
  if (!response || !localStorage.getItem("token")) return;
  const method = (response?.config?.method || "").toLowerCase();
  if (!MUTATING_METHODS.includes(method)) return;

  const directBalance = extractWalletBalance(response);
  if (directBalance !== undefined) {
    applyWalletBalance(directBalance);
    return;
  }

  const url = response?.config?.url || "";
  if (WALLET_BALANCE_ENDPOINTS.some((endpoint) => endpoint && url.startsWith(endpoint))) {
    scheduleWalletRefresh();
  }
};

const handleResponseStatus = async (response: any) => {
  if (response?.status === 200 || response?.status === 201) {
    maybeSyncWalletBalance(response);
  }
  toast.clearWaitingQueue();
  // Resolve the toast text generically for every endpoint: a Super Admin
  // override for response.data.messageKey wins, falling back to the
  // backend's own response.data.message, then a generic message.
  const resolveMessage = (defaultMessage?: any) =>
    getMessage(response?.data?.messageKey, defaultMessage);
  switch (response?.status) {
    case 200:
      // only show success toast for specific endpoints to avoid excessive toasts
      if (response.config.url.startsWith(end_points.authConfirm_Login_Api.url)) {
        if (response.data?.data?.subscription_status === commonWords.inactive) return;
      }
      //
      if (
        response.config.url.startsWith(end_points.resendOtpApi.url) ||
        response.config.url.startsWith(end_points.writtenContent_delete_Api.url) ||
        response.config.url.startsWith(end_points.ssoLogin_Api.url) ||
        response.config.url.startsWith(end_points.authConfirm_Login_Api.url)
      ) {
        response?.data?.message &&
          toast.success(resolveMessage(response?.data?.message), {
            className: "custom-toast-success",
            bodyClassName: "custom-toast-body-success",
            progressClassName: "custom-toast-progress-success",
          });
      }
      break;
    case 201:
      if (
        response.config.url.startsWith(end_points.master_provider_catgeory_listApi.url) ||
        response.config.url.startsWith(end_points.transcriptionStatus.url) ||
        response.config.url.startsWith(end_points.retryTranscription.url) ||
        response.config.url.startsWith(end_points.startTranscription.url)
      ) {
        return;
      } else if (
        response.config.url.startsWith(end_points.authLoginApi.url) &&
        response?.data?.data?.two_factor_enabled !== undefined &&
        response?.data?.data?.two_factor_enabled
      ) {
        return;
      } else
        response?.data?.message &&
          toast.success(resolveMessage(response?.data?.message), {
            className: "custom-toast-success",
            bodyClassName: "custom-toast-body-success",
            progressClassName: "custom-toast-progress-success",
          });
      break;

    case 422:
      // NPI lookup handles its own messaging (warning toast + manual fallback),
      // so suppress the global error toast for this endpoint only.
      if (response.config.url.startsWith(end_points.userByNpi.url)) {
        return;
      }
      response?.data?.validation
        ? toast.error(resolveMessage(response?.data?.validation?.[0]?.msg), {
            className: "custom-toast-error",
            bodyClassName: "custom-toast-body-error",
            progressClassName: "custom-toast-progress-error",
          })
        : Array.isArray(response?.data?.message)
          ? toast.error(resolveMessage(response?.data?.message?.[0]?.msg), {
              className: "custom-toast-error",
              bodyClassName: "custom-toast-body-error",
              progressClassName: "custom-toast-progress-error",
            })
          : toast.error(resolveMessage(response?.data?.message), {
              className: "custom-toast-error",
              bodyClassName: "custom-toast-body-error",
              progressClassName: "custom-toast-progress-error",
            });
      break;
    case 402:
      if (response.config.url.startsWith(end_points.get_slug_status.url)) {
        return;
      } else
        toast.error(resolveMessage(response?.data?.message || response?.data?.data?.message), {
          className: "custom-toast-error",
          bodyClassName: "custom-toast-body-error",
          progressClassName: "custom-toast-progress-error",
        });
      break;
    case 403:
      if (response.config.url.startsWith(end_points.client_ticket_update.url)) {
        toast.error(resolveMessage(response?.data?.message || response?.data?.data?.message), {
          className: "custom-toast-error",
          bodyClassName: "custom-toast-body-error",
          progressClassName: "custom-toast-progress-error",
        });
      } else {
        return;
      }

      break;
    case 404:
      toast.error(resolveMessage(response?.data?.message), {
        className: "custom-toast-error",
        bodyClassName: "custom-toast-body-error",
        progressClassName: "custom-toast-progress-error",
        autoClose: 6000,
      });
      break;
    case 409:
      if (
        response.config.url.startsWith(end_points.startTranscription.url) ||
        response.config.url.startsWith(end_points.retryTranscription.url)
      ) {
        return; // handled by service logging; suppress toast for background operations
      }
      toast.error(resolveMessage(response?.data?.message), {
        className: "custom-toast-error",
        bodyClassName: "custom-toast-body-error",
        progressClassName: "custom-toast-progress-error",
      });
      break;
    case 400:
      if (
        response.config.url.startsWith(end_points.writtenContent_Create_Api.url) ||
        response.config.url.startsWith(end_points?.profile_about_addlink_create.url) ||
        response.config.url.startsWith(end_points.updateSubscriptionPlanApi.url) ||
        response.config.url.startsWith(end_points.onetime_payment_create.url) ||
        response.config.url.startsWith(end_points.meeting_get_waiting_room_by_slug.url) ||
        response.config.url.startsWith(end_points.donation_create_payment_intent.url) ||
        response.config.url.startsWith(end_points.get_singleArticle_Details.url) ||
        response.config.url.startsWith(end_points.written_content_views_count_update_Api.url) ||
        response.config.url.startsWith(end_points.startTranscription.url) ||
        response.config.url.startsWith(end_points.retryTranscription.url)
      ) {
        return;
      } else {
        const locationAccessRequired = LOCATION_RESPONSE_HINT_ENDPOINTS.some((url) =>
          response.config.url.startsWith(url),
        );
        if (locationAccessRequired) {
          toast.info(resolveMessage(response?.data?.message), {
            className: "custom-toast-info location-access-required",
            bodyClassName: "custom-toast-body-info",
            progressClassName: "custom-toast-progress-info",
          });
        } else {
          toast.error(resolveMessage(response?.data?.message), {
            className: "custom-toast-error",
            bodyClassName: "custom-toast-body-error",
            progressClassName: "custom-toast-progress-error",
          });
        }
      }
      break;
    case 500:
      toast.error(resolveMessage(response?.data?.message || "Internal server error"), {
        className: "custom-toast-error",
        bodyClassName: "custom-toast-body-error",
        progressClassName: "custom-toast-progress-error",
      });
      break;
    case 401:
      if (
        response.config.url.startsWith(end_points.join_golive.url) ||
        response.config.url.startsWith(end_points.peek_invite.url)
      ) {
        // Guest invite flows authenticate with a static bearer token, not a
        // real user session — a 401 here means "invalid email/invite", not
        // "session expired". Let the page show its own inline error instead
        // of forcing a logout + redirect to home.
        return;
      }
      if (response.config.url.startsWith(end_points.add_favorite_content.url)) {
        toast.error(resolveMessage(response?.data?.message || "Unauthorized"), {
          className: "custom-toast-error",
          bodyClassName: "custom-toast-body-error",
          progressClassName: "custom-toast-progress-error",
        });
        setTimeout(() => {
          store.dispatch(removeAuthToken());
          // goToLogin();
        }, 1000);
      } else {
        toast.error(resolveMessage(response?.data?.message || "Unauthorized"), {
          className: "custom-toast-error",
          bodyClassName: "custom-toast-body-error",
          progressClassName: "custom-toast-progress-error",
        });
        setTimeout(() => {
          store.dispatch(removeAuthToken());
          goToLogin();
        }, 1000);
      }
      break;
    case 410:
      toast.error(resolveMessage(response?.data?.message), {
        className: "custom-toast-error",
        bodyClassName: "custom-toast-body-error",
        progressClassName: "custom-toast-progress-error",
      });
      break;
    default:
      break;
  }
};

export default axiosInstance;
